/**
 * Rollen und Faehigkeiten – reine Regeln, ohne Oberflaeche und ohne Speicher.
 *
 * Diese Datei weiss NICHTS darueber, wer gerade angemeldet ist oder wie man
 * sich anmeldet. Sie beantwortet nur die Frage "darf diese Rolle das?".
 * Das ist Absicht: wenn spaeter eine echte Anmeldung dazukommt, aendert sich
 * hier keine Zeile, und die Oberflaeche fragt weiterhin nur nach
 * Faehigkeiten statt nach Rollen.
 *
 * Fuer die Oberflaeche gilt: NIE `role === 'admin'` abfragen, immer
 * `can('admin.view')`. Sonst muss man bei einer neuen Rolle jede Stelle
 * anfassen.
 */

export type Role = 'admin' | 'member' | 'viewer';

/** Was man in der App tun koennen kann. */
export type Capability =
  /** Den Wartungsbereich ueberhaupt sehen. */
  | 'admin.view'
  /** Gefaehrliche Wartung: alles zuruecksetzen, Zwischenspeicher leeren. */
  | 'admin.maintain'
  /** Benutzer und Rollen verwalten (braucht spaeter die Anmeldung). */
  | 'admin.users'
  /** Inhalte anlegen und aendern. */
  | 'content.edit'
  /** Inhalte loeschen. */
  | 'content.delete';

/**
 * Welche Rolle darf was.
 *
 * Bewusst als Tabelle und nicht als verschachtelte if-Abfragen: so sieht man
 * auf einen Blick, was eine Rolle kann, und eine neue Rolle ist eine Zeile.
 */
const GRANTS: Record<Capability, readonly Role[]> = {
  'admin.view': ['admin'],
  'admin.maintain': ['admin'],
  'admin.users': ['admin'],
  'content.edit': ['admin', 'member'],
  'content.delete': ['admin', 'member'],
};

export function roleCan(role: Role, capability: Capability): boolean {
  return GRANTS[capability].includes(role);
}

/** Alle Faehigkeiten einer Rolle – fuer die Anzeige im Admin-Bereich. */
export function capabilitiesOf(role: Role): Capability[] {
  return (Object.keys(GRANTS) as Capability[]).filter((c) => roleCan(role, c));
}

/** Klartext je Faehigkeit – fuer die Rechte-Uebersicht im Admin-Bereich. */
export const CAPABILITY_LABELS: Record<Capability, string> = {
  'admin.view': 'Admin-Bereich öffnen',
  'admin.maintain': 'Wartung: Sicherung, Zwischenspeicher, alles löschen',
  'admin.users': 'Anfragen annehmen, Benutzer sperren, Rechte vergeben',
  'content.edit': 'Songs, Setlists, Alben und Band-Bibel anlegen und bearbeiten',
  'content.delete': 'Inhalte löschen',
};

export const ROLE_LABELS: Record<Role, string> = {
  admin: 'Administrator',
  member: 'Mitglied',
  viewer: 'Nur lesen',
};

/** Ein Mensch, der die App benutzt. */
export interface User {
  id: string;
  name: string;
  role: Role;
  /** Deaktivierte Benutzer behalten ihre Daten, kommen aber nicht hinein. */
  active: boolean;
  /** E-Mail, soweit der Anbieter eine kennt – zum Erkennen von Anfragen. */
  email?: string;
  /**
   * Von einem Administrator gesperrt oder abgelehnt. Unterscheidet
   * "gesperrt" von "wartet auf Freischaltung" – beide sind nicht aktiv.
   */
  blocked?: boolean;
  /** Worker-Stufe 1–3 (siehe LEVELS). Fehlt bei aelteren Konten → levelOf. */
  level?: Level;
  /** Freigegebene Bereiche. Fehlt = alle (aeltere Konten, nichts geht verloren). */
  areas?: Partial<Record<AreaId, boolean>>;
}

/* ------------------------------------------------ Stufen und Bereiche */

/**
 * Worker-Stufen (Entscheidung des Inhabers, September 2026):
 *   1  nur ansehen
 *   2  anlegen und aendern
 *   3  zusaetzlich loeschen und Freigaben aendern
 * Administratoren haben immer alles. Durchgesetzt in firestore.rules
 * (level(), inArea()); die Oberflaeche zeigt nur, was erlaubt ist.
 */
export type Level = 1 | 2 | 3;

export const LEVELS: { level: Level; label: string; text: string }[] = [
  { level: 1, label: 'Stufe 1', text: 'nur ansehen' },
  { level: 2, label: 'Stufe 2', text: 'anlegen und ändern' },
  { level: 3, label: 'Stufe 3', text: 'dazu löschen und teilen' },
];

/** Bereiche, die der Admin je Person freischaltet. System ist nur fuer Admins. */
export type AreaId = 'home' | 'cloud' | 'leadSheets';

export const AREAS: { id: AreaId; label: string }[] = [
  { id: 'home', label: 'Schnittstelle Rot' },
  { id: 'cloud', label: 'Cloud' },
  { id: 'leadSheets', label: 'Lead Sheets' },
];

/** Stufe eines Kontos. Aeltere Konten ohne Feld: Mitglied = 3 (wie bisher), Nur lesen = 1. */
export function levelOf(user: Pick<User, 'role' | 'level'>): Level {
  if (user.role === 'admin') return 3;
  if (user.level === 1 || user.level === 2 || user.level === 3) return user.level;
  return user.role === 'member' ? 3 : 1;
}

/** Rolle passend zur Stufe (die Regeln lesen beides). */
export function roleForLevel(level: Level): Role {
  return level === 1 ? 'viewer' : 'member';
}

/** Darf dieses Konto in den Bereich? Admins immer, aeltere Konten ohne Feld auch. */
export function hasArea(user: Pick<User, 'role' | 'areas'>, area: AreaId): boolean {
  if (user.role === 'admin' || !user.areas) return true;
  return user.areas[area] === true;
}
